import { type ReactNode } from 'react'
import { JobsListEmpty } from '@/features/jobs/components/JobsListEmpty'
import { Button } from '@/components/Button'
import { SkeletonList } from '@/components/Skeleton'
import {
  TableActions,
  TableEmptyView,
  TableScroll,
  TableSearch,
  TableTabSkeleton,
  TableTabs,
  TableToolbar,
  TableView,
} from '@/components/DataTable/TableView'
import { getInitials } from '@/utils/image-util'
import styles from './JobList.module.css'

export interface JobListSelectionToggle {
  isShowing: boolean
  onToggleShowing: () => void
}

export interface JobListProps {
  /**
   * The saved filter tabs.
   */
  tabs: ReactNode
  /**
   * Shown above the table while a shared tab is open.
   */
  sharedTabBanner: ReactNode
  searchText: string
  onSearchChange: (value: string) => unknown
  isProPerspective: boolean
  filterButton: ReactNode
  displayMenu: ReactNode
  selection?: JobListSelectionToggle
  onExport: () => void
  moreMenu: ReactNode
  appliedFilters: ReactNode
  isEmpty: boolean
  onClearSearchFilter: () => void
  onSetJobScope?: () => void
  exportDialog: ReactNode
  corporateMode: boolean
  children: ReactNode
}

export function JobList({
  tabs,
  sharedTabBanner,
  searchText,
  onSearchChange,
  isProPerspective,
  filterButton,
  displayMenu,
  selection,
  onExport,
  moreMenu,
  appliedFilters,
  isEmpty,
  onClearSearchFilter,
  onSetJobScope,
  exportDialog,
  corporateMode,
  children,
}: JobListProps) {
  return (
    <TableView>
      <TableToolbar>
        {tabs}
        <TableActions>
          <TableSearch
            value={searchText}
            onChange={onSearchChange}
            placeholder={
              isProPerspective ? 'Search jobs or clients' : 'Search jobs'
            }
            testId="job-search-field"
          />
          {filterButton}
          {displayMenu}
          {selection && (
            <Button
              label="Select"
              size="small"
              iconLeft="select"
              aria-pressed={selection.isShowing}
              onPress={selection.onToggleShowing}
              testId="job-list-toggle-selection"
            />
          )}
          <Button
            label="Export"
            size="small"
            iconLeft="download"
            onPress={onExport}
            testId="job-management-table-menu-export-csv"
          />
          {moreMenu}
        </TableActions>
      </TableToolbar>
      {sharedTabBanner}
      {appliedFilters}
      <TableScroll>
        {isEmpty ? (
          <TableEmptyView>
            <JobsListEmpty
              onClearSearchFilter={onClearSearchFilter}
              onSetJobScope={onSetJobScope}
              corporateMode={corporateMode}
            />
          </TableEmptyView>
        ) : (
          children
        )}
      </TableScroll>
      {exportDialog}
    </TableView>
  )
}

export interface JobListSkeletonProps {
  table: ReactNode
}

export interface SaveAsTabProps {
  /**
   * Why a tab can't be added, or `null` when it can.
   */
  hint: string | null
  onSave: () => void
}

/**
 * Saves the selected tab plus the applied filters as a new tab.
 */
export function SaveAsTab({ hint, onSave }: SaveAsTabProps) {
  return (
    <>
      {hint && <span className={styles.bannerHint}>{hint}</span>}
      <Button
        label="Save as tab"
        size="small"
        isDisabled={hint !== null}
        onPress={onSave}
        testId="job-list-save-as-tab"
      />
    </>
  )
}

export interface SharedTabBannerProps {
  sharedBy: string
  name: string
  /**
   * Why the tab can't be added, or `null` when it can.
   */
  addHint: string | null
  onAdd: () => void
  onDismiss: () => void
}

/**
 * Says who shared the open tab, with a way to keep it.
 */
export function SharedTabBanner({
  sharedBy,
  name,
  addHint,
  onAdd,
  onDismiss,
}: SharedTabBannerProps) {
  return (
    <div className={styles.banner} data-test="job-list-shared-tab-banner">
      <span className={styles.bannerAvatar} aria-hidden="true">
        {getInitials(sharedBy).toUpperCase()}
      </span>
      <span className={styles.bannerText}>
        <b>{sharedBy}</b> shared “{name}”
      </span>
      {addHint && <span className={styles.bannerHint}>{addHint}</span>}
      <Button
        label="Dismiss"
        variant="text"
        size="small"
        onPress={onDismiss}
        testId="job-list-shared-tab-dismiss"
      />
      <Button
        label="Add to my tabs"
        variant="primary"
        size="small"
        isDisabled={addHint !== null}
        onPress={onAdd}
        testId="job-list-shared-tab-add"
      />
    </div>
  )
}

/**
 * Holds the list's place while the page loads.
 */
export function JobListSkeleton({ table }: JobListSkeletonProps) {
  return (
    <TableView>
      <TableToolbar>
        <TableTabs>
          <SkeletonList count={4}>
            <TableTabSkeleton />
          </SkeletonList>
        </TableTabs>
      </TableToolbar>
      <TableScroll>{table}</TableScroll>
    </TableView>
  )
}
